import { User } from "../general/user";

export enum UserStatusType {
  REGISTERED = "Registered",
  INVITATION_PENDING = "Invitation pending",
  ARCHIVED_PENDING = "Archived pending",
  INVITED = "Invited",
  ACTIVE = "Active",
  ARCHIVED = "Archived",
}

export enum Roles {
  SYSTEM_ADMIN = "System Admin",
  MAIN_ADMIN = "Main Admin",
  PORTAL_ADMIN = "Portal Admin",
  ADMIN = "Admin",
  PORTAL_CONFIG = "Portal Config",
  REPORT_VIEWER = "Report Viewer",
  USER_MNG = "User Management",
  EMAIL_MNG = "Email Management",
  ASSIGNMENT_MNG = "Assignment Management",
  FILE_HUB = "File Hub",
  END_USER = "End User",
  USER_IMPORT_JOB = "User Import Job",
}

export enum RoleLevel {
  END_USER = 1,
  MENU_ITEM = 2,
  ADMIN = 3,
  PORTAL_ADMIN = 4,
  MAIN_ADMIN = 5,
  SYSTEM_ADMIN = 6,
}
export interface RoleOption {
  role: Roles;
  level: number;
  isDisabled: boolean;
}

export interface GetRolesForUserResult {
  dropdownDisabled: boolean;
  roles: RoleOption[];
}

export interface AttributeType {
  PortalUserAttributeUid: string;
  attributeKey: string;
  dataType: string;
  attributeOrder: number;
  attributeValue?: string | null;
  isRequired: boolean;
}

export type GetUsersPaginatedResult = {
  count: number;
  previousPage: string;
  nextPage: string;
  rows: GetUserResult[];
};

export interface GetUserResult {
  UserPortalUid: string;
  status: UserStatusType;
  statusTypes?: UserStatusType[];
  thumbnail?: string;
  firstName: string;
  lastName: string;
  email: string;
  roles?: Roles[];
  attributes?: AttributeType[];
  portals?: UserPortalMapping[];
}

export interface UserPortalMapping {
  portalUid: string;
  name: string;
  roles?: Roles[];
}

export interface UserPortalMappingsByEmailQueryParams {
  email: string;
}

export interface UserQueryParams {
  search?: string;
  userAttributeKey?: string;
  userAttributeValue?: string;
  limit?: number;
  offset?: number;
  orderBy?: string;
  sort?: string;
  operator?:
    | StringOperator
    | NumericOperator
    | DateOperator
    | BooleanOperator
    | ListOperator
    | "";
}

export interface UserFilterQueryParams {
  userAttributeKey?: string;
  userAttributeValue?: string;
  operator?:
    | StringOperator
    | NumericOperator
    | DateOperator
    | BooleanOperator
    | ListOperator
    | "";
}

export interface CreateUserBodyParams {
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
  status: UserStatusType;
  thumbnail?: string;
  additionalAttributes?: AttributeType[];
}

export interface UpdateUserBodyParams {
  UserPortalUid: string;
  cognitoUser?: string;
  status?: UserStatusType;
  thumbnail?: string;
  firstName?: string;
  lastName?: string;
  roles?: string[];
  activatedAt?: Date;
  additionalAttributes?: AttributeType[];
}

export type GetPortalUserAttributesPaginatedResult = {
  count: number;
  previousPage: string;
  nextPage: string;
  rows: GetPortalUserAttributeResult[];
};

export type GetPortalUserAttributeResult = {
  portalUserAttributeUid: string;
  attributeName: string;
  "portalUserAttributeTranslations.attributeName": string;
  attributeKey: string;
  dataType: string;
  isRequired: boolean;
  isAvailable: boolean;
  isEditable?: boolean;
  isActive?: boolean;
  attributeOrder: number;
  isLineManager?: boolean;
};

export enum StringOperator {
  IN = "in",
  NOT_IN = "notIn",
  CONTAINS = "contains",
  STARTS_WITH = "startsWith",
  ENDS_WITH = "endsWith",
  NOT_CONTAINS = "notContains",
}

export enum NumericOperator {
  EQUAL = "eq",
  NOT_EQUAL = "ne",
  LESS_THAN = "lt",
  LESS_THAN_EQUAL = "lte",
  GREATER_THAN = "gt",
  GREATER_THAN_EQUAL = "gte",
}

export enum DateOperator {
  EQUAL = "eqd",
  NOT_EQUAL = "ned",
  LESS_THAN = "ltd",
  LESS_THAN_EQUAL = "lted",
  GREATER_THAN = "gtd",
  GREATER_THAN_EQUAL = "gted",
  BETWEEN = "betweend",
}

export enum BooleanOperator {
  IS = "is",
  IS_NOT = "isNot",
}

export enum ListOperator {
  IN = "in",
  NOT_IN = "notIn",
}

export interface AdvancedSearchFilter {
  attribute: string;
  operator:
    | StringOperator
    | NumericOperator
    | DateOperator
    | BooleanOperator
    | ListOperator
    | "";
  value: string;
  groupHeader?: string;
  filterGroup: string;
}

export interface AdvancedSearchBodyParams {
  selections: AdvancedSearchSelection[];
  ruleId?: string;
  assignmentId?: string;
}

export type UserListItem = Pick<
  User,
  "UserPortalUid" | "firstName" | "lastName" | "email" | "status"
>;

export interface AdvancedSearchSelection {
  filters: AdvancedSearchFilter[];
}

export interface AdvancedDdlQueryParams {
  languageCode: string;
  assignmentFields?: boolean;
}

export interface AdvancedDdlItems {
  filterGroup: string;
  attributeKey: string;
  attributeName: string;
  dataType: string;
  result?: string;
}

export interface AdvancedDdlResult {
  groupHeader: string;
  items: AdvancedDdlItems[];
}

export interface SelectedUserItems {
  id: string;
  name: string;
}

// Profile API types
export interface UserPathParams {
  portalId: string;
  userId: string;
}

export interface ProfileAttributeType {
  PortalUserAttributeUid: string;
  attributeKey: string;
  dataType: string;
  attributeOrder: string;
  attributeValue?: string | null;
  isRequired: boolean;
  isEditable: boolean;
}

export interface GetUserProfileResult {
  UserPortalUid: string;
  status: UserStatusType;
  firstName: string;
  lastName: string;
  email: string;
  thumbnailUrl?: string;
  roles?: Roles[] | Roles;
  attributes?: ProfileAttributeType[];
}

export interface ProfileAttributeUpdateType {
  PortalUserAttributeUid: string;
  attributeKey: string;
  attributeValue?: string | null;
}

export interface UpdateProfileBodyParams {
  thumbnailUrl?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  additionalAttributes?: ProfileAttributeUpdateType[];
}
